import type { TShortcutElement } from '@alpod-ai-writer/plate-plugins/shortcuts';
import { DocumentSectionType } from '@alpod-ai-writer/shared';
import { cn,withRef } from '@udecode/cn';
import { IS_APPLE } from '@udecode/plate';
import { PlateElement, useFocused, useReadOnly,useSelected } from '@udecode/plate/react';
import React, { useCallback } from 'react';

import { useMounted } from '../../../hooks/useMounted.hook';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useSectionNav } from '../../hooks/useSectionNav.hook';
import { useShortcutList } from '../../hooks/useShortcutList.hook';

interface ShortcutElementProps {
    prefix?: string;
    onClick?: (mentionNode: any) => void;
}

export const ShortcutElement = withRef<typeof PlateElement, ShortcutElementProps>(({
    children,
    className,
    prefix,
    ...props
}, ref) => {
    const editor = useDocumentEditor();
    const { shortcutMap } = useShortcutList();
    const { selectSectionByType } = useSectionNav(editor.template.config.sections);

    const element = props.element as TShortcutElement;
    const selected = useSelected();
    const focused = useFocused();
    const mounted = useMounted();
    const readOnly = useReadOnly();

    const handleOnClick = useCallback(() => {
        if (element.ref) {
            selectSectionByType(DocumentSectionType.ShortcutList, element.ref);
        }
    }, [ selectSectionByType, element.ref ]);

    const label = shortcutMap.get(element.ref)?.shortcut ?? `(zkratka byla smazána @${element.ref})`;

    return (
        <PlateElement
            as={'span'}
            ref={ref}
            className={cn(
                className,
                'inline-block rounded-full bg-slate-200 hover:bg-blue-200 px-1.5 py-0.5 align-baseline text-sm font-medium transition-colors duration-200',
                !readOnly && 'cursor-pointer',
                selected && focused && 'ring-2 ring-ring',
                element.children[0].bold === true && 'font-bold',
                element.children[0].italic === true && 'italic',
                element.children[0].underline === true && 'underline'
            )}
            onClick={handleOnClick}
            data-slate-value={element.ref}
            contentEditable={false}
            draggable
            {...props}
        >
            {mounted && IS_APPLE ? (
                // Mac OS IME https://github.com/ianstormtaylor/slate/issues/3490
                <React.Fragment>
                    {children}
                    {prefix}
                    {label}
                </React.Fragment>
            ) : (
                // Others like Android https://github.com/ianstormtaylor/slate/pull/5360
                <React.Fragment>
                    {prefix}
                    {label}
                    {children}
                </React.Fragment>
            )}
        </PlateElement>
    );
});
